import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  Sparkles, Calculator, Clock, BookOpen, Download, Smartphone,
  Palette, BarChart3, Database, Check, X, Hourglass, Lock,
} from "lucide-react";
import ModuleHint from "@/components/ModuleHint";

// Map icon string from API → component
const ICON = {
  Calculator, Clock, BookOpen, Download, Smartphone, Palette,
  BarChart3, Database, Sparkles,
};

export default function Erweiterungen() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState("");
  const [noteFor, setNoteFor] = useState(null);
  const [note, setNote] = useState("");

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/extensions");
      setItems(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Laden fehlgeschlagen");
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const g = {};
    for (const e of items) (g[e.category] = g[e.category] || []).push(e);
    return g;
  }, [items]);

  async function submitRequest() {
    if (!noteFor) return;
    setRequesting(noteFor.key);
    try {
      await api.post("/extensions/request", {
        extension_key: noteFor.key, note: note || "",
      });
      toast.success("Anfrage gesendet — Super-Admin wurde benachrichtigt");
      setNoteFor(null); setNote("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Anfrage fehlgeschlagen");
    } finally { setRequesting(""); }
  }

  if (user && !["admin", "geschaeftsfuehrung"].includes(user.role) && !user.is_super_admin) {
    return (
      <div className="p-8 text-zinc-400" data-testid="ext-no-access">
        Nur Admins können Erweiterungen verwalten.
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="erweiterungen-page">
      <ModuleHint moduleKey="erweiterungen" />
      <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl font-semibold text-zinc-100 inline-flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-emerald-400" />
            Erweiterungen
          </h2>
          <p className="text-xs text-zinc-500 mt-1">
            Optionale Add-ons für Ihre Plattform. Aktivierte Erweiterungen sind sofort einsatzbereit.
            Nicht aktive Erweiterungen können bei Ihrem Super-Admin angefragt werden.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="text-zinc-500 text-sm p-8 text-center">Lade Erweiterungen…</div>
      ) : items.length === 0 ? (
        <div className="text-zinc-500 text-sm p-8 text-center">
          Aktuell sind keine Erweiterungen verfügbar.
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([cat, list]) => (
            <div key={cat}>
              <h3 className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-2">{cat}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {list.map(e => <ExtensionCard key={e.key} ext={e}
                  busy={requesting === e.key}
                  onRequest={() => { setNoteFor(e); setNote(""); }} />)}
              </div>
            </div>
          ))}
        </div>
      )}

      {noteFor && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="ext-request-modal">
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg w-full max-w-md p-5">
            <h3 className="text-lg font-semibold text-zinc-100 mb-1">Anfrage senden</h3>
            <p className="text-xs text-zinc-500 mb-4">{noteFor.name}</p>
            <textarea value={note} onChange={(e) => setNote(e.target.value)}
              placeholder={`Optionale Notiz an Super-Admin (z.B. "Wir möchten 14 Tage testen")`}
              rows={4} data-testid="ext-request-note"
              className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-100" />
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => { setNoteFor(null); setNote(""); }}
                className="px-4 py-2 text-sm bg-zinc-800 hover:bg-zinc-700 rounded">Abbrechen</button>
              <button onClick={submitRequest} disabled={requesting}
                data-testid="ext-request-submit"
                className="px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 disabled:bg-zinc-800 text-white rounded font-medium">
                {requesting ? "Sende…" : "Anfrage senden"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ExtensionCard({ ext, onRequest, busy }) {
  const Icon = ICON[ext.icon] || Sparkles;
  const ent = ext.entitlement;
  const isActive = ext.is_active;
  const isPending = !!ext.pending_request;
  const isContract = ent?.included_by_contract;

  let statusBadge = null;
  if (isActive && isContract) {
    statusBadge = <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/40">Im Vertrag</span>;
  } else if (isActive && ent?.status === "trial") {
    statusBadge = <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-blue-500/15 text-blue-300 border border-blue-500/40">Trial</span>;
  } else if (isActive) {
    statusBadge = <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/40">Aktiv</span>;
  } else if (isPending) {
    statusBadge = <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/40">Anfrage offen</span>;
  } else {
    statusBadge = <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-zinc-800 text-zinc-400">Nicht aktiv</span>;
  }

  return (
    <div data-testid={`ext-card-${ext.key}`}
      className={`rounded-lg border p-4 transition ${isActive ? "border-emerald-500/30 bg-emerald-500/5"
                                                            : "border-zinc-800 bg-zinc-900"}`}>
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-md border flex items-center justify-center shrink-0 ${
          isActive ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                   : "border-zinc-800 bg-zinc-950 text-zinc-500"
        }`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="font-semibold text-zinc-100 leading-tight">{ext.name}</div>
            {statusBadge}
          </div>
          <p className="text-xs text-zinc-400 mt-1.5 leading-relaxed line-clamp-3">{ext.description}</p>
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between text-xs">
        <div>
          {ext.monthly_price_eur > 0 ? (
            <>
              <span className="font-mono text-zinc-100">{ext.monthly_price_eur.toFixed(0)} €</span>
              <span className="text-zinc-500"> / Monat</span>
              {ext.yearly_price_eur > 0 && (
                <span className="text-zinc-500"> · {ext.yearly_price_eur.toFixed(0)} €/Jahr</span>
              )}
            </>
          ) : (
            <span className="text-zinc-500">Inklusive</span>
          )}
        </div>
        {isActive ? (
          <span className="inline-flex items-center gap-1 text-emerald-400 text-[11px]">
            <Check className="w-3.5 h-3.5" /> Verfügbar
          </span>
        ) : isPending ? (
          <span className="inline-flex items-center gap-1 text-amber-400 text-[11px]" data-testid={`ext-pending-${ext.key}`}>
            <Hourglass className="w-3.5 h-3.5" /> Wartet
          </span>
        ) : (
          <button onClick={onRequest} disabled={busy}
            data-testid={`ext-request-${ext.key}`}
            className="px-3 py-1 text-xs bg-blue-600 hover:bg-blue-700 disabled:bg-zinc-800 text-white rounded font-medium inline-flex items-center gap-1">
            {ext.trial_enabled ? `${ext.trial_days}-Tage Trial anfragen` : "Anfrage senden"}
          </button>
        )}
      </div>

      {ent?.expiry_at && (
        <div className="mt-2 text-[10px] text-amber-400 inline-flex items-center gap-1">
          <Lock className="w-3 h-3" /> Läuft ab am {String(ent.expiry_at).slice(0, 10)}
        </div>
      )}
    </div>
  );
}
